"use client";

import Link from "next/link";
import { STUDY_PASSES } from "../../../lib/pricingPlans";
import { track } from "../../../lib/analytics";

// Homepage pricing preview, round 3: Free Tracker plus the two study
// passes (the primary way to buy under the founder-led rebrand). The
// monthly Pro/Max tiers still exist but live on /pricing as the
// month-to-month option; leading with passes here keeps the strip
// aligned with the prep-window story. Pass copy/prices come from
// lib/pricingPlans.ts STUDY_PASSES so this strip never drifts from the
// /pricing source of truth. Copy is DRAFT pending the wording review
// (docs/landing-copy-review.md).

// Record-only free tier: this line must match the /pricing comparison
// table (weak areas, analytics, and the planner are paid). Log it,
// explain it, keep it organized -- interpretation is what you pay for.
const FREE_SUMMARY =
  "Unlimited question logging, your own reflections, full history, and export. No card.";

export default function PricingStrip() {
  return (
    <section className="py-20 sm:py-24 bg-maren-stone dark:bg-maren-night border-t border-maren-sage/30 dark:border-maren-sage/20">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-maren-green dark:text-maren-gold">
            Pricing
          </p>
          <h2 className="mt-4 font-display font-medium tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-[1.15] text-maren-charcoal dark:text-maren-stone">
            Track free. Pay once for the months you are studying.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6">
          {/* Free Tracker card */}
          <div className="relative rounded-2xl border border-maren-sage/40 dark:border-maren-sage/20 bg-white dark:bg-maren-charcoal p-6 sm:p-7 flex flex-col">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-maren-green dark:text-maren-gold">
              Free Tracker
            </p>
            <p className="mt-2 font-display text-3xl sm:text-4xl font-medium text-maren-charcoal dark:text-maren-stone leading-none">
              $0 forever
            </p>
            <p className="mt-4 text-sm text-maren-charcoal/75 dark:text-maren-stone/80 leading-relaxed min-h-[3.5rem]">
              {FREE_SUMMARY}
            </p>
            <Link
              href="/signup"
              onClick={() => track({ name: "cta_click", props: { location: "pricing_strip_free" } })}
              className="mt-6 inline-flex items-center justify-center rounded-lg bg-maren-green hover:bg-maren-green/90 active:bg-maren-green/80 px-5 py-3 text-sm font-semibold text-maren-stone shadow-sm transition focus:outline-none focus:ring-2 focus:ring-maren-gold/50"
            >
              Start Free
            </Link>
          </div>

          {/* Study pass cards */}
          {STUDY_PASSES.map((pass) => (
            <div
              key={pass.id}
              className={`relative rounded-2xl p-6 sm:p-7 flex flex-col ${
                pass.featured
                  ? "border-2 border-maren-gold dark:border-maren-gold bg-white dark:bg-maren-charcoal shadow-lg shadow-maren-gold/10"
                  : "border border-maren-sage/40 dark:border-maren-sage/20 bg-white dark:bg-maren-charcoal"
              }`}
            >
              {pass.featured && (
                <span className="absolute -top-3 left-6 rounded-full bg-maren-gold px-3 py-0.5 text-[10px] font-bold uppercase tracking-wide text-maren-charcoal">
                  Best value
                </span>
              )}
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-maren-green dark:text-maren-gold">
                {pass.name}
              </p>
              <p className="mt-2 font-display text-3xl sm:text-4xl font-medium text-maren-charcoal dark:text-maren-stone leading-none">
                {pass.price} <span className="text-base font-normal text-maren-charcoal/60 dark:text-maren-stone/60">once</span>
              </p>
              <p className="mt-1 text-xs text-maren-charcoal/60 dark:text-maren-stone/60">{pass.perMonth}</p>
              <p className="mt-4 text-sm text-maren-charcoal/75 dark:text-maren-stone/80 leading-relaxed min-h-[3.5rem]">
                {pass.description}
              </p>
              <Link
                href="/pricing"
                onClick={() => track({ name: "cta_click", props: { location: `pricing_strip_${pass.id}` } })}
                className={`mt-6 inline-flex items-center justify-center rounded-lg px-5 py-3 text-sm font-semibold transition ${
                  pass.featured
                    ? "bg-maren-green hover:bg-maren-green/90 active:bg-maren-green/80 text-maren-stone shadow-sm focus:outline-none focus:ring-2 focus:ring-maren-gold/50"
                    : "border-2 border-maren-green text-maren-green dark:border-maren-gold dark:text-maren-gold hover:bg-maren-green hover:text-maren-stone dark:hover:bg-maren-gold dark:hover:text-maren-charcoal"
                }`}
              >
                See what is included
              </Link>
            </div>
          ))}
        </div>

        {/* Risk reversal at the money moment: refund clarity measurably
            lifts conversion with skeptical students, and "never
            auto-renew" answers the top pass objection before it forms. */}
        <p className="mt-8 text-center text-sm text-maren-charcoal/70 dark:text-maren-stone/75">
          Passes never auto-renew. A
          3-month pass costs less than a single session with most MCAT tutors.
        </p>

        <p className="mt-4 text-center text-sm text-maren-charcoal/65 dark:text-maren-stone/70">
          Prefer month to month? A{" "}
          <Link
            href="/pricing#max"
            className="text-maren-green dark:text-maren-gold font-semibold hover:underline underline-offset-4"
          >
            monthly option
          </Link>{" "}
          is available on the pricing page, along with feature lists and the refund policy.
        </p>
      </div>
    </section>
  );
}
